import React from 'react';
import { LeadSource, LeadStatus } from '../../types';
import { LeadsDateRange } from '../../utils/leadsHubUtils';
import {
  Users,
  Sparkles,
  Inbox,
  Phone,
  CheckCircle2,
  XCircle,
  Heart,
  Calendar,
  Eye,
  EyeOff,
} from 'lucide-react';
import '../ops/OpsSidebar.css';
import './LeadsSidebar.css';

export type LeadsSidebarView = LeadStatus | 'all' | 'hot' | 'archived';

interface LeadsSidebarProps {
  isOpen?: boolean;
  activeView: LeadsSidebarView;
  onSelectView: (view: LeadsSidebarView) => void;
  statusCounts: Record<string, number>;
  dateRange: LeadsDateRange;
  onDateRangeChange: (range: LeadsDateRange) => void;
  sourceFilter: LeadSource | 'all';
  onSourceFilterChange: (source: LeadSource | 'all') => void;
  showArchived: boolean;
  onToggleShowArchived: () => void;
}

const STATUS_ITEMS: { id: LeadsSidebarView; label: string; icon: React.ReactNode }[] = [
  { id: 'all', label: 'All Leads', icon: <Users size={15} /> },
  { id: 'hot', label: 'Hot Leads', icon: <Sparkles size={15} /> },
  { id: 'new', label: 'New', icon: <Inbox size={15} /> },
  { id: 'contacted', label: 'Contacted', icon: <Phone size={15} /> },
  { id: 'qualified', label: 'Qualified', icon: <CheckCircle2 size={15} /> },
  { id: 'nurture', label: 'Nurture', icon: <Heart size={15} /> },
  { id: 'lost', label: 'Lost', icon: <XCircle size={15} /> },
  { id: 'out-of-scope', label: 'Out of scope', icon: <EyeOff size={15} /> },
];

const DATE_RANGES: { id: LeadsDateRange; label: string }[] = [
  { id: 'all', label: 'All time' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: '1y', label: 'Last year' },
  { id: 'ytd', label: 'This calendar year' },
];

const SOURCE_OPTIONS: { id: LeadSource | 'all'; label: string }[] = [
  { id: 'all', label: 'All sources' },
  { id: 'web-form', label: 'Web Form' },
  { id: 'facebook-form', label: 'Facebook Form' },
  { id: 'instagram-form', label: 'Instagram Form' },
  { id: 'referral', label: 'Referral' },
  { id: 'call-in', label: 'Call-in' },
  { id: 'cold-call', label: 'Cold Call' },
  { id: 'unspecified', label: 'Unspecified' },
];

const LeadsSidebar: React.FC<LeadsSidebarProps> = ({
  isOpen = false,
  activeView,
  onSelectView,
  statusCounts,
  dateRange,
  onDateRangeChange,
  sourceFilter,
  onSourceFilterChange,
  showArchived,
  onToggleShowArchived,
}) => {
  return (
    <aside className={`ops-sidebar leads-sidebar ${isOpen ? 'is-open' : ''}`} data-tour="leads-sidebar">
      <div className="ops-sidebar-header">
        <span className="ops-sidebar-label">Leads</span>
      </div>

      <nav className="ops-sidebar-nav">
        {STATUS_ITEMS.map(item => {
          const isActive = activeView === item.id;
          const count =
            item.id === 'all'
              ? statusCounts.all || 0
              : item.id === 'hot'
                ? statusCounts.hot || 0
                : statusCounts[item.id] || 0;
          return (
            <button
              key={item.id}
              type="button"
              className={`ops-sidebar-item ${isActive ? 'active' : ''}`}
              onClick={() => onSelectView(item.id)}
            >
              <span className="ops-sidebar-item-icon">{item.icon}</span>
              <span className="ops-sidebar-item-label">{item.label}</span>
              {count > 0 && <span className="ops-sidebar-item-count">{count}</span>}
            </button>
          );
        })}
      </nav>

      <div className="leads-sidebar-filters">
        <span className="leads-sidebar-filter-label">
          <Calendar size={12} /> Date range
        </span>
        <select
          className="leads-sidebar-select"
          value={dateRange}
          onChange={e => onDateRangeChange(e.target.value as LeadsDateRange)}
          aria-label="Date range filter"
        >
          {DATE_RANGES.map(r => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>

        <span className="leads-sidebar-filter-label">Source</span>
        <select
          className="leads-sidebar-select"
          value={sourceFilter}
          onChange={e => onSourceFilterChange(e.target.value as LeadSource | 'all')}
          aria-label="Source filter"
        >
          {SOURCE_OPTIONS.map(s => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        className={`ops-sidebar-toggle-completed ${showArchived ? 'showing' : ''}`}
        onClick={onToggleShowArchived}
      >
        {showArchived ? <Eye size={13} /> : <EyeOff size={13} />}
        <span>{showArchived ? 'Hide archived' : 'Show archived'}</span>
      </button>
    </aside>
  );
};

export default LeadsSidebar;
